/**
 * Category colour.
 *
 * A subject should look the same everywhere it appears — the same blue on the
 * "Design" card in the catalogue, the "Design" chip on the course page and the
 * "Design" row in a list. Assigning that by hand across sixty courses would
 * drift immediately, so the colour is derived from the category name itself:
 * the same string always lands on the same accent, and a new category added in
 * the admin picks one up without any code change.
 *
 * The six accents come from the logo — its chart bars are orange, gold, cyan and
 * rose above a blue head — so the palette reads as this brand rather than as a
 * generic rainbow. Every pairing below has been measured: `ink` on `soft` clears
 * 4.5:1, `ink` on the page background clears 4.5:1, and each `solid` carries the
 * text colour that clears 4.5:1 against it — white for blue, rose and green; the
 * ink for gold, orange and cyan, which are too light to hold white type.
 */

export type AccentName = 'blue' | 'gold' | 'orange' | 'cyan' | 'rose' | 'green';

export interface AccentClasses {
  name: AccentName;
  /** Tinted surface + readable ink + matching hairline. For chips and cards. */
  chip: string;
  /** Just the tinted surface. */
  soft: string;
  /** Readable text colour on a light surface. */
  text: string;
  /**
   * Full-strength fill with a text colour that clears 4.5:1 against it. Blue,
   * rose and green take white; gold, orange and cyan are too light for white at
   * full strength and take the ink instead. The class carries its own text
   * colour precisely so a caller cannot pair them wrongly.
   */
  solid: string;
  /** A 3px rule or left border — the lightest possible way to colour a row. */
  rule: string;
  /** Decorative gradient-free wash for a card's media area. */
  wash: string;
}

const ACCENTS: Record<AccentName, AccentClasses> = {
  blue: {
    name: 'blue',
    chip: 'bg-accent-blue-soft text-accent-blue-ink border-accent-blue-mid',
    soft: 'bg-accent-blue-soft',
    text: 'text-accent-blue-ink',
    solid: 'bg-accent-blue text-white',
    rule: 'bg-accent-blue',
    wash: 'bg-accent-blue-soft text-accent-blue-ink',
  },
  gold: {
    name: 'gold',
    chip: 'bg-accent-gold-soft text-accent-gold-ink border-accent-gold-mid',
    soft: 'bg-accent-gold-soft',
    text: 'text-accent-gold-ink',
    solid: 'bg-accent-gold text-[#17181C]', // 9.9:1 — the logo's own pairing
    rule: 'bg-accent-gold',
    wash: 'bg-accent-gold-soft text-accent-gold-ink',
  },
  orange: {
    name: 'orange',
    chip: 'bg-accent-orange-soft text-accent-orange-ink border-accent-orange-mid',
    soft: 'bg-accent-orange-soft',
    text: 'text-accent-orange-ink',
    solid: 'bg-accent-orange text-[#17181C]', // 6.3:1 — white would be 2.8:1
    rule: 'bg-accent-orange',
    wash: 'bg-accent-orange-soft text-accent-orange-ink',
  },
  cyan: {
    name: 'cyan',
    chip: 'bg-accent-cyan-soft text-accent-cyan-ink border-accent-cyan-mid',
    soft: 'bg-accent-cyan-soft',
    text: 'text-accent-cyan-ink',
    solid: 'bg-accent-cyan text-[#17181C]', // 5.4:1 — white would be 3.3:1
    rule: 'bg-accent-cyan',
    wash: 'bg-accent-cyan-soft text-accent-cyan-ink',
  },
  rose: {
    name: 'rose',
    chip: 'bg-accent-rose-soft text-accent-rose-ink border-accent-rose-mid',
    soft: 'bg-accent-rose-soft',
    text: 'text-accent-rose-ink',
    solid: 'bg-accent-rose text-white',
    rule: 'bg-accent-rose',
    wash: 'bg-accent-rose-soft text-accent-rose-ink',
  },
  green: {
    name: 'green',
    chip: 'bg-accent-green-soft text-accent-green-ink border-accent-green-mid',
    soft: 'bg-accent-green-soft',
    text: 'text-accent-green-ink',
    solid: 'bg-accent-green text-white',
    rule: 'bg-accent-green',
    wash: 'bg-accent-green-soft text-accent-green-ink',
  },
};

const ORDER: AccentName[] = ['blue', 'gold', 'orange', 'cyan', 'rose', 'green'];

/** Small stable string hash — same input, same accent, across reloads and machines. */
const hash = (value: string): number => {
  let h = 0;
  for (let i = 0; i < value.length; i += 1) {
    h = (h << 5) - h + value.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
};

/** The accent for a category, subject or tag. Falls back to blue for empty input. */
export const accentFor = (key: string | undefined | null): AccentClasses => {
  if (!key) return ACCENTS.blue;
  return ACCENTS[ORDER[hash(key.trim().toLowerCase()) % ORDER.length]];
};

/** Pick a specific accent by name, for places that are not category-driven. */
export const accent = (name: AccentName): AccentClasses => ACCENTS[name];

/** Accents in display order — for legends and filter rows. */
export const accentOrder = ORDER;
